import { ref, readonly, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useChartNotesV2 } from '@/composables/useChartNotesV2';
import { useUniqueIds } from '@/composables/useUniqueIds';
import { useChartStyleJsonCache } from '@/composables/useChartStyleJsonCache';
import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
import { extractChartStyleObject } from '@/composables/extractChartStyleObject';
import { useHasChartInitialized } from '@/composables/useHasChartInitialized';
import { useUrlParameters } from '@/composables/useUrlParameters';
import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
import { INVALID_CHART_STYLE_RECID, isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
import { getChartFromCache } from '@/composables/getChartFromCache';
import { areChartStyleObjectsEqual } from '@/composables/areChartStyleObjectsEqual';
import { getStyleUrlSearchParams } from '@/utils/getStyleUrlSearchParams';
import { cloneProxyObject } from '@/utils/cloneProxyObject';
import { isValidNumber } from '@/utils/isValidNumber';
import { Chart, isChart, ChartStyle } from '@/interfaces/chart';
import { APP_VERSION_NUMBER } from '@/enums/app-version-number';

const { VITE_APP_PUBLIC_PATH } = import.meta.env;
let hasInitializedBrowserTabHistory = false;
const SESSION_STORAGE_SC3_WORKBENCH_BROWSER_TAB_HISTORY_INDEXES_KEY = 'sc3WorkbenchBrowserTabHistoryIndexesKey';
const canGoForwardInBrowserTabHistory = ref(false);
const canGoBackInBrowserTabHistory = ref(false);

interface BrowserTabState {
  timeStamp: number;
  appVersionNumber?: any;
  url: string;
  recidOfChartBeingViewed: number;
  recidOfLoadSymbolOnlyChartBeingViewed: number;
  recidOfChartStyleBeingViewed: number;
  chartStoreChart: Chart;
  sc3WorkbenchBrowserTabHistoryIndex: number;
};

function isBrowserTabState(unknownValue: unknown): unknownValue is BrowserTabState {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'timeStamp' in unknownValue &&
    'url' in unknownValue &&
    'recidOfChartBeingViewed' in unknownValue &&
    'recidOfLoadSymbolOnlyChartBeingViewed' in unknownValue &&
    'recidOfChartStyleBeingViewed' in unknownValue &&
    'chartStoreChart' in unknownValue &&
    'sc3WorkbenchBrowserTabHistoryIndex' in unknownValue &&
    typeof unknownValue.timeStamp === 'number' &&
    typeof unknownValue.url === 'string' &&
    typeof unknownValue.recidOfChartBeingViewed === 'number' &&
    typeof unknownValue.recidOfLoadSymbolOnlyChartBeingViewed === 'number' &&
    typeof unknownValue.recidOfChartStyleBeingViewed === 'number' &&
    isChart(unknownValue.chartStoreChart) &&
    typeof unknownValue.sc3WorkbenchBrowserTabHistoryIndex === 'number'
  );
}

function setBrowserTabHistoryConfiguration(): void {
  try {
    window.history.scrollRestoration = 'manual';
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error while attempting to set browser tab history configuration.', e);
  }
}

function initializeBrowserTabHistory(): void {
  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { chartStyleJsonBeingViewed, chartStyleJsonOfChartBeingViewed, registerChartStyleJsonCacheUpdateCallback } = useChartStyleJsonCache();
  const { chartBeingViewed, styleCacheIdOfChartBeingViewed } = useWorkbenchSelections();

  if (hasInitializedBrowserTabHistory) {
    return;
  }

  hasInitializedBrowserTabHistory = true;

  window.sc3WorkbenchLogger.info('Initializing browser tab history.');

  setBrowserTabHistoryConfiguration();

  window.addEventListener('popstate', handlePopStateEvent);
  document.body.addEventListener('keydown', handleKeyDownEvent);

  const { recidOfChartBeingViewed, symbol } = storeToRefs(chartStore);
  const { i: styleCacheIdBeingViewed } = storeToRefs(layoutStore);
  const { chartLists, chartStyles, sampleChartStyleGroups } = storeToRefs(savedChartsStore);

  const symbolOfChartBeingViewed = computed(() => chartBeingViewed.value?.symbol ?? '');

  watch(recidOfChartBeingViewed, updateUrlParameters);
  watch(symbol, updateUrlParameters);
  watch(chartBeingViewed, updateUrlParameters);
  watch(symbolOfChartBeingViewed, updateUrlParameters);
  watch(styleCacheIdOfChartBeingViewed, updateUrlParameters);
  watch(styleCacheIdBeingViewed, updateUrlParameters);
  watch(chartStyleJsonBeingViewed, updateUrlParameters);
  watch(chartStyleJsonOfChartBeingViewed, updateUrlParameters);
  watch(chartLists, updateUrlParameters);
  watch(chartStyles, updateUrlParameters);
  watch(sampleChartStyleGroups, updateUrlParameters);

  registerChartStyleJsonCacheUpdateCallback(updateUrlParameters);
}

function getBrowserTabState(): BrowserTabState | null {
  try {
    return isBrowserTabState(window.history.state) ? window.history.state : null;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error while attempting to access browser tab state.', e);
  }

  return null;
}

function getSc3WorkbenchBrowserTabHistoryIndexesArray(): number[] {
  const sc3WorkbenchBrowserTabHistoryIndexesString = window.sessionStorage.getItem(SESSION_STORAGE_SC3_WORKBENCH_BROWSER_TAB_HISTORY_INDEXES_KEY);

  if (!sc3WorkbenchBrowserTabHistoryIndexesString) {
    return [];
  }

  const parsedSc3WorkbenchBrowserTabHistoryIndexesString = JSON.parse(sc3WorkbenchBrowserTabHistoryIndexesString);

  if (!Array.isArray(parsedSc3WorkbenchBrowserTabHistoryIndexesString)) {
    return [];
  }

  if (parsedSc3WorkbenchBrowserTabHistoryIndexesString.find(i => (typeof i !== 'number' && typeof i !== 'string') || !isValidNumber(i, { isRequiredToBeInteger: true }))) {
    return [];
  }

  return parsedSc3WorkbenchBrowserTabHistoryIndexesString.map(Number);
}

function setCurrentBrowserTabHistoryIndex(index: number, shouldClearHistoryStackAfterIndex: boolean): void {
  try {
    let sc3WorkbenchBrowserTabHistoryIndexesArray = getSc3WorkbenchBrowserTabHistoryIndexesArray();

    if (!sc3WorkbenchBrowserTabHistoryIndexesArray.includes(index)) {
      sc3WorkbenchBrowserTabHistoryIndexesArray.push(index);
    }

    if (shouldClearHistoryStackAfterIndex) {
      sc3WorkbenchBrowserTabHistoryIndexesArray = sc3WorkbenchBrowserTabHistoryIndexesArray.filter(i => i <= index);
    }

    canGoForwardInBrowserTabHistory.value = sc3WorkbenchBrowserTabHistoryIndexesArray.includes(index + 1);
    canGoBackInBrowserTabHistory.value = sc3WorkbenchBrowserTabHistoryIndexesArray.includes(index - 1);

    sc3WorkbenchBrowserTabHistoryIndexesArray.sort((a, b) => a - b);

    const sc3WorkbenchBrowserTabHistoryIndexesString = JSON.stringify(sc3WorkbenchBrowserTabHistoryIndexesArray);

    window.sessionStorage.setItem(SESSION_STORAGE_SC3_WORKBENCH_BROWSER_TAB_HISTORY_INDEXES_KEY, sc3WorkbenchBrowserTabHistoryIndexesString);

    return;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error while attempting to set the current browser tab history index.', e);
  }

  canGoForwardInBrowserTabHistory.value = false;
  canGoBackInBrowserTabHistory.value = false;

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_SC3_WORKBENCH_BROWSER_TAB_HISTORY_INDEXES_KEY, '');
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Error while attempting to reset the ${SESSION_STORAGE_SC3_WORKBENCH_BROWSER_TAB_HISTORY_INDEXES_KEY} sessionStorage value to empty string.`, e);
  }
}

function getBrowserTabHistoryLength(): number {
  try {
    return window.history.length;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while checking the browser tab history length.', e);
    return 1;
  }
}

function getUrlString(browserTabState: BrowserTabState): string {
  const { getChartStyleJsonFromCache } = useChartStyleJsonCache();
  const { getPerfChartSymbolsString } = usePerfChartSymbols();
  const {
    getSymbolUrlParameterValue,
    getStyleCacheIdUrlParameterValue,
    getChartRecidUrlParameterValue,
    getStyleCacheSettingsOverridesUrlParameterValues,
    removeAllNonChartRelatedURLParameters
  } = useUrlParameters();

  const currentUrlQueryString = window.location.search;
  const currentUrlSearchParams = new URLSearchParams(currentUrlQueryString);
  let newUrlSearchParams = new URLSearchParams(`s=${browserTabState.chartStoreChart.symbol}`);
  const cachedChartBeingViewed = getChartFromCache(browserTabState.recidOfChartBeingViewed);
  const chartBeingViewedChartStyleJson = getChartStyleJsonFromCache(cachedChartBeingViewed?.styleCacheId ?? '');

  removeAllNonChartRelatedURLParameters(currentUrlSearchParams);

  // If the current recidOfChartBeingViewed is valid but we're unable to load the chart object, that means
  // 1) There is an issue bug somewhere on the back/front-end that's preventing us from successfully fetching this valid chart object
  // 2) OR This chart object used to exist but has since been deleted (perhaps the recidOfChartBeingViewed came from an old bookmark)
  // 3) OR The recidOfChartBeingViewed actually belongs to a different user (perhaps the recidOfChartBeingViewed came from a permalink)
  // 4) OR There is a timing issue and the chart object associated with the current recidOfChartBeingViewed hasn't been loaded into
  //    the cache yet, but it hopefully will in just a moment
  // 5) OR some combination of any of the above
  // 6) OR we have the chart object and its symbol matchings the current symbol
  // In all the above cases, we want to set the "a" URL parameter because it can't do any real (worse) harm in any of the above cases,
  // and if it's a timing issue as in #4 above and this "a" URL parameter is actually invalid for the current state of things then the
  // "a" parameter will get removed once the chart object is loaded into the cache and the URL parameters are updated again
  if (
    isValidChartRecid(browserTabState.recidOfChartBeingViewed) &&
    (!cachedChartBeingViewed || cachedChartBeingViewed.symbol === browserTabState.chartStoreChart.symbol)
  ) {
    newUrlSearchParams.set('a', browserTabState.recidOfChartBeingViewed.toString());
  }

  // If the current recidOfChartBeingViewed is valid, we have the chart object, its styleCacheId starts with a "p", the current symbol
  // matches that of the chart being viewed, we've successfully loaded the XML for this chart's pCode, and we see that there are
  // currently no unsaved changes to this saved chart, THEN we're safe to set all possible URL parameters
  if (
    cachedChartBeingViewed?.styleCacheId.startsWith('p') &&
    cachedChartBeingViewed.symbol === browserTabState.chartStoreChart.symbol &&
    chartBeingViewedChartStyleJson &&
    areChartStyleObjectsEqual({ ...chartBeingViewedChartStyleJson, ...cachedChartBeingViewed.styleCacheSettingsOverrides }, browserTabState.chartStoreChart)
  ) {
    newUrlSearchParams.set('a', browserTabState.recidOfChartBeingViewed.toString());

    newUrlSearchParams = getStyleUrlSearchParams({
      ...browserTabState.chartStoreChart,
      perfChartSymbolsString: getPerfChartSymbolsString(browserTabState.chartStoreChart.perfChartSymbols),
      i: cachedChartBeingViewed.styleCacheId
    }, newUrlSearchParams);

    return `${VITE_APP_PUBLIC_PATH}?${newUrlSearchParams.toString()}`;
  }

  const currentBrowserTabState = getBrowserTabState();

  // If the app is still roughly in the same state as in the current (soon to be previous) browser tab state, and we can't identify
  // anything about the current (soon to be previous) browser tab state that's now become invalid, then we can just leave the URL
  // (i.e. URL parameters) as-is
  if (
    currentBrowserTabState &&
    areChartStyleObjectsEqual(currentBrowserTabState.chartStoreChart, browserTabState.chartStoreChart) &&
    currentBrowserTabState.chartStoreChart.symbol === browserTabState.chartStoreChart.symbol &&
    currentBrowserTabState.recidOfChartBeingViewed === browserTabState.recidOfChartBeingViewed &&
    (!cachedChartBeingViewed || cachedChartBeingViewed.symbol === browserTabState.chartStoreChart.symbol) &&
    (!chartBeingViewedChartStyleJson || !cachedChartBeingViewed || areChartStyleObjectsEqual({ ...chartBeingViewedChartStyleJson, ...cachedChartBeingViewed.styleCacheSettingsOverrides }, browserTabState.chartStoreChart))
  ) {
    currentUrlSearchParams.set('s', browserTabState.chartStoreChart.symbol);

    return `${VITE_APP_PUBLIC_PATH}?${currentUrlSearchParams.toString()}`;
  }

  const chartRecidFromCurrentUrl = getChartRecidUrlParameterValue(currentUrlQueryString);
  const cachedChartFromCurrentUrl = getChartFromCache(chartRecidFromCurrentUrl);
  const chartStyleFromCurrentUrlChartRecid: Partial<ChartStyle> = getChartStyleJsonFromCache(cachedChartFromCurrentUrl?.styleCacheId ?? '') ?? {};
  const styleCacheIdFromCurrentUrl = getStyleCacheIdUrlParameterValue(currentUrlQueryString);
  const chartStyleFromCurrentUrlStyleCacheId: Partial<ChartStyle> = getChartStyleJsonFromCache(styleCacheIdFromCurrentUrl) ?? {};
  const styleCacheSettingsOverrides: Partial<ChartStyle> = getStyleCacheSettingsOverridesUrlParameterValues(currentUrlQueryString);
  const symbolFromCurrentUrl = getSymbolUrlParameterValue(currentUrlQueryString);
  const chartStyleFromCurrentUrl: ChartStyle = {
    ...browserTabState.chartStoreChart,
    ...chartStyleFromCurrentUrlChartRecid,
    ...chartStyleFromCurrentUrlStyleCacheId,
    ...styleCacheSettingsOverrides
  };

  // Try our best to piece together the chart that the current (soon to be previous) URL parameters correspond to, if it
  // results in the same chart as what the user is currently viewing, then we can just leave the URL (i.e. URL parameters) as-is
  if (
    areChartStyleObjectsEqual(chartStyleFromCurrentUrl, browserTabState.chartStoreChart) &&
    (!symbolFromCurrentUrl || symbolFromCurrentUrl === browserTabState.chartStoreChart.symbol)
  ) {
    currentUrlSearchParams.set('s', browserTabState.chartStoreChart.symbol);

    return `${VITE_APP_PUBLIC_PATH}?${currentUrlSearchParams.toString()}`;
  }

  return `${VITE_APP_PUBLIC_PATH}?${newUrlSearchParams.toString()}`;
}

function setBrowserTabState(browserTabState: BrowserTabState, isReplacingCurrentBrowserTabState: boolean): void {
  initializeBrowserTabHistory();

  const urlString = getUrlString(browserTabState);

  try {
    if (isReplacingCurrentBrowserTabState) {
      window.history.replaceState(browserTabState, '', urlString);
    } else {
      window.history.pushState(browserTabState, '', urlString);
    }

    setCurrentBrowserTabHistoryIndex(browserTabState.sc3WorkbenchBrowserTabHistoryIndex, !isReplacingCurrentBrowserTabState);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Error while attempting to ${isReplacingCurrentBrowserTabState ? 'replace current' : 'push new'} browser tab state.`, e);

    canGoForwardInBrowserTabHistory.value = false;
    canGoBackInBrowserTabHistory.value = false;
  }
}

function updateUrlParameters(): void {
  addNewEntryToBrowserTabHistory(false);
}

function addNewEntryToBrowserTabHistory(forceReplacementOfCurrentBrowserTabState: boolean): void {
  const chartStore = useChartStore();
  const { hasLoadedInitialChartData } = useHasChartInitialized();

  if (!hasLoadedInitialChartData.value && !forceReplacementOfCurrentBrowserTabState) {
    return;
  }

  initializeBrowserTabHistory();

  const newBrowserTabState = {
    timeStamp: Date.now(),
    appVersionNumber: APP_VERSION_NUMBER,
    url: document.URL,
    recidOfChartBeingViewed: chartStore.recidOfChartBeingViewed,
    recidOfLoadSymbolOnlyChartBeingViewed: chartStore.recidOfLoadSymbolOnlyChartBeingViewed,
    recidOfChartStyleBeingViewed: chartStore.recidOfChartStyleBeingViewed,
    chartStoreChart: cloneProxyObject(chartStore.chart)
  };

  const currentBrowserTabState = getBrowserTabState();

  if (
    !currentBrowserTabState ||
    forceReplacementOfCurrentBrowserTabState ||
    (
      areChartStyleObjectsEqual(currentBrowserTabState.chartStoreChart, newBrowserTabState.chartStoreChart) &&
      currentBrowserTabState.chartStoreChart.symbol === newBrowserTabState.chartStoreChart.symbol
    )
  ) {
    setBrowserTabState({
      ...newBrowserTabState,
      sc3WorkbenchBrowserTabHistoryIndex: currentBrowserTabState ? currentBrowserTabState.sc3WorkbenchBrowserTabHistoryIndex : getBrowserTabHistoryLength() - 1
    }, true);
    return;
  }

  setBrowserTabState({
    ...newBrowserTabState,
    sc3WorkbenchBrowserTabHistoryIndex: currentBrowserTabState.sc3WorkbenchBrowserTabHistoryIndex + 1
  }, false);
}

async function loadChartFromBrowserTabHistory(): Promise<void> {
  const chartStore = useChartStore();
  const { getPositiveUniqueId } = useUniqueIds();
  const { populateChartStyleJsonCacheForChart, populateChartStyleJsonCacheForChartStyle } = useChartStyleJsonCache();
  const { getSymbolInformation } = useSymbolInformation();

  initializeBrowserTabHistory();

  try {
    window.sc3WorkbenchLogger.info('Attempting to load chart from browser tab history...');

    const currentBrowserTabState = getBrowserTabState();

    if (!currentBrowserTabState) {
      throw new Error(`currentBrowserTabState is invalid.`);
    }

    const chartStyle = extractChartStyleObject(currentBrowserTabState.chartStoreChart);
    const chartSymbol = currentBrowserTabState.chartStoreChart.symbol;

    for (const indicator of chartStyle.sortedIndicators) {
      indicator.id = getPositiveUniqueId();
    }

    for (const overlay of chartStyle.sortedOverlays) {
      overlay.id = getPositiveUniqueId();
    }

    let recidOfChartBeingViewed = INVALID_CHART_RECID;

    if (isValidChartRecid(currentBrowserTabState.recidOfChartBeingViewed)) {
      recidOfChartBeingViewed = currentBrowserTabState.recidOfChartBeingViewed;
      const cachedChartBeingViewed = getChartFromCache(recidOfChartBeingViewed);

      if (cachedChartBeingViewed && cachedChartBeingViewed.symbol !== chartSymbol) {
        recidOfChartBeingViewed = INVALID_CHART_RECID;
      }
    }

    let recidOfLoadSymbolOnlyChartBeingViewed = INVALID_CHART_RECID;

    if (isValidChartRecid(currentBrowserTabState.recidOfLoadSymbolOnlyChartBeingViewed)) {
      recidOfLoadSymbolOnlyChartBeingViewed = currentBrowserTabState.recidOfLoadSymbolOnlyChartBeingViewed;
      const cachedLoadSymbolOnlyChartBeingViewed = getChartFromCache(recidOfLoadSymbolOnlyChartBeingViewed);

      if (cachedLoadSymbolOnlyChartBeingViewed && cachedLoadSymbolOnlyChartBeingViewed.symbol !== chartSymbol) {
        recidOfLoadSymbolOnlyChartBeingViewed = INVALID_CHART_RECID;
      }
    }

    if (
      isValidChartRecid(recidOfChartBeingViewed) &&
      isValidChartRecid(recidOfLoadSymbolOnlyChartBeingViewed) &&
      recidOfChartBeingViewed !== recidOfLoadSymbolOnlyChartBeingViewed
    ) {
      recidOfChartBeingViewed = INVALID_CHART_RECID;
    }

    if (
      isValidChartRecid(recidOfChartBeingViewed) &&
      !isValidChartRecid(recidOfLoadSymbolOnlyChartBeingViewed)
    ) {
      recidOfLoadSymbolOnlyChartBeingViewed = recidOfChartBeingViewed;
    }

    let recidOfChartStyleBeingViewed = INVALID_CHART_STYLE_RECID;

    if (isValidChartStyleRecid(currentBrowserTabState.recidOfChartStyleBeingViewed, true)) {
      recidOfChartStyleBeingViewed = currentBrowserTabState.recidOfChartStyleBeingViewed;
    }

    window.sc3WorkbenchLogger.info(`Loading chart from browser tab history with recidOfChartBeingViewed: ${recidOfChartBeingViewed} and recidOfLoadSymbolOnlyChartBeingViewed: ${recidOfLoadSymbolOnlyChartBeingViewed} and recidOfChartStyleBeingViewed: ${recidOfChartStyleBeingViewed} and symbol: ${chartSymbol}.`, chartStyle);

    chartStore.$patch({
      ...chartStyle,
      symbol: chartSymbol,
      recidOfChartBeingViewed,
      recidOfLoadSymbolOnlyChartBeingViewed,
      recidOfChartStyleBeingViewed,
      latestLoadBrowserTabHistoryChartTimeStamp: Date.now()
    });

    setCurrentBrowserTabHistoryIndex(currentBrowserTabState.sc3WorkbenchBrowserTabHistoryIndex, false);

    await Promise.all([
      populateChartStyleJsonCacheForChart(recidOfChartBeingViewed),
      populateChartStyleJsonCacheForChartStyle(recidOfChartStyleBeingViewed),
      getSymbolInformation(chartSymbol).catch((e) => {
        window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${chartSymbol} while attempting to load chart from browser tab history.`, e);
      })
    ]);
  } catch (e) {
    window.sc3WorkbenchLogger.info('Failed to load chart from browser tab history.', e);

    throw new Error('Failed to load chart from browser tab history', { cause: e });
  }
}

async function handlePopStateEvent(e: PopStateEvent): Promise<void> {
  const { forceCloseChartNotesV2 } = useChartNotesV2();

  forceCloseChartNotesV2();

  await loadChartFromBrowserTabHistory();
}

function handleKeyDownEvent(e: KeyboardEvent): void {
  const layoutStore = useLayoutStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();

  if (
    (!e.ctrlKey && !e.metaKey) ||
    layoutStore.modalType ||
    layoutStore.isDragNDropping ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  if (
    e.key.toUpperCase() === 'Y' &&
    !e.shiftKey
  ) {
    goForwardInBrowserTabHistory();
    return;
  }

  if (e.key.toUpperCase() !== 'Z') {
    return;
  }

  if (e.shiftKey) {
    goForwardInBrowserTabHistory();
    return;
  }

  goBackInBrowserTabHistory();
}

function goForwardInBrowserTabHistory(): void {
  if (!canGoForwardInBrowserTabHistory.value) {
    return;
  }

  initializeBrowserTabHistory();

  try {
    window.history.forward();
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while attempting to go forward in browser tab history.', e);
  }
}

function goBackInBrowserTabHistory(): void {
  if (!canGoBackInBrowserTabHistory.value) {
    return;
  }

  initializeBrowserTabHistory();

  try {
    window.history.back();
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while attempting to go back in browser tab history.', e);
  }
}

export function useBrowserTabHistory() {
  return {
    addNewEntryToBrowserTabHistory,
    loadChartFromBrowserTabHistory,
    goForwardInBrowserTabHistory,
    goBackInBrowserTabHistory,
    canGoForwardInBrowserTabHistory: readonly(canGoForwardInBrowserTabHistory),
    canGoBackInBrowserTabHistory: readonly(canGoBackInBrowserTabHistory)
  };
}
